import { FC, useMemo } from "react";

import CloseIcon from "@mui/icons-material/Close";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import { Translate } from "@/components/Application/Translate";
import { StepLogLine } from "@/components/Procedures/Checklist/Step/StepLogLine";
import { IStepLog } from "@/components/Procedures/Checklist/utils/Types";

import {
  useFetchProcedureStepLog,
  useFetchSuggestionsByChecklistId,
} from "@/service/ProcedureQueries";

export const StepLog: FC<IStepLog> = (props) => {
  const { toggleShowStepLog, showStepLog, activeSchemeId, step } = props;
  const theme = useTheme();

  // still fetch behind the flag like before
  const { data: log } = useFetchProcedureStepLog(showStepLog, activeSchemeId, step?.Id);
  const { data: suggestionLog } = useFetchSuggestionsByChecklistId(activeSchemeId);

  if (!showStepLog) return null;

  const entries = log?.Entries ?? [];
  const stepSuggestions = useMemo(
    () => (Array.isArray(suggestionLog) ? suggestionLog.filter((s) => s.StepId === step?.Id) : []),
    [suggestionLog, step?.Id],
  );

  const hasAnything = entries.length > 0 || stepSuggestions.length > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        mb: 1,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: "action.hover",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <HistoryOutlinedIcon fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700}>
            <Translate String="Procedure.Checklist.Step.Log.Heading">
              {hasAnything ? "Activity log" : "No activity"}
            </Translate>
          </Typography>

          {/* quick counters */}
          <Stack direction="row" spacing={1}>
            <Chip
              size="small"
              label={`${entries.length} ${entries.length === 1 ? "entry" : "entries"}`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`${stepSuggestions.length} suggestion${
                stepSuggestions.length === 1 ? "" : "s"
              }`}
            />
          </Stack>
        </Stack>

        <IconButton
          aria-label="Close log"
          onClick={toggleShowStepLog}
          size="small"
          sx={{ ml: 1 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {/* Body */}
      <Box sx={{ px: 2, py: 1.5 }}>
        {!hasAnything && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            <Translate String="Procedure.Checklist.Step.Log.Empty">
              There’s no activity for this step yet.
            </Translate>
          </Typography>
        )}

        {/* Entries list */}
        {entries.length > 0 && (
          <Stack spacing={1.25}>
            {entries.map((entry) => (
              <StepLogLine key={entry.Id} logLine={entry} />
            ))}
          </Stack>
        )}

        {/* Optional divider before future inline “suggestions” section (if you later want to render them here) */}
        {/* {stepSuggestions.length > 0 && entries.length > 0 && <Divider sx={{ my: 1.5 }} /> } */}
      </Box>
    </Paper>
  );
};
